import React from 'react';
import { Loader2 } from 'lucide-react';

interface PanelShellProps {
  children: React.ReactNode;
  loading?: boolean;
  error?: string | null;
  empty?: boolean;
  emptyMessage?: string;
  className?: string;
}

/**
 * Shared wrapper for mobile sub-panel content.
 * Provides consistent scroll container, loading, error, and empty states.
 * All colors from CSS variables — no hardcoded values.
 */
export function PanelShell({
  children,
  loading = false,
  error = null,
  empty = false,
  emptyMessage = 'Nothing here yet',
  className = '',
}: PanelShellProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2
          size={28}
          className="animate-spin"
          style={{ color: 'var(--color-accent)' }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 px-6">
        <span className="text-sm" style={{ color: 'var(--text-color)', opacity: 0.6 }}>
          {error}
        </span>
      </div>
    );
  }

  if (empty) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 px-6">
        <span className="text-sm" style={{ color: 'var(--text-color)', opacity: 0.5 }}>
          {emptyMessage}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`h-full overflow-y-auto overscroll-contain ${className}`}
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {children}
    </div>
  );
}

/**
 * Section header inside a panel — used for grouping content.
 */
export function PanelSection({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="px-4 py-3">
      {title && (
        <h3
          className="text-xs font-semibold uppercase tracking-wider mb-3"
          style={{ color: 'var(--text-color)', opacity: 0.45 }}
        >
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}

/**
 * Generic "coming soon" placeholder for panels that aren't built yet.
 */
export function ComingSoonPanel({ label, icon: Icon }: { label: string; icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }> }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 px-6">
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center"
        style={{ background: 'var(--card-bg)' }}
      >
        <Icon size={24} style={{ color: 'var(--color-accent)' }} />
      </div>
      <span className="text-base font-semibold" style={{ color: 'var(--text-color)' }}>
        {label}
      </span>
      <span className="text-xs text-center" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
        Coming soon
      </span>
    </div>
  );
}
